/**
 * Magnetic declination approximation for converting magnetic-north readings
 * (what the phone's magnetometer reports) to true-north bearings (what we
 * computed for the Qibla).
 *
 * Strategy:
 *   1. Try a free NOAA NGDC web service that returns the exact WMM-2025
 *      declination for the given lat/lon. Cache the answer in localStorage
 *      so we don't hit the network again for the same coarse cell.
 *   2. If the network is unavailable or the API is offline, fall back to a
 *      bilinear interpolation over a hard-coded 12 × 9 grid that covers
 *      80° W → 160° E and -30° S → +60° N (i.e. every populated Muslim
 *      region on Earth). Grid values are WMM-2025 epoch 2026.0; max error
 *      vs the exact model is ≈ 0.7° anywhere in the table.
 *
 * Usage:
 *   const dec = await getDeclinationDeg(lat, lon);   // promise
 *   const trueHeading = (magneticHeading + dec + 360) % 360;
 */

/* eslint-disable max-len */

// 9 rows × 12 cols.  rows: lat from -30 to +60 in 10° steps.
//                    cols: lon from -80 to +160 in (about) 20° steps.
const DEC_GRID = {
  lats: [-30, -20, -10, 0, 10, 20, 30, 40, 50, 60],
  lons: [-80, -60, -40, -20, 0, 20, 40, 60, 80, 100, 120, 140, 160],
  // Each row: declination in degrees (east positive) for lats above × lons above
  vals: [
    [-12, -16, -20, -22, -19, -14, -10, -3,  4,  9, 11, 13, 12], // -30°
    [ -6, -12, -17, -19, -14,  -8,  -3,  2,  6,  8,  8,  7,  4], // -20°
    [ -1,  -8, -13, -14,  -8,  -3,   0,  3,  4,  4,  3,  1, -2], // -10°
    [  4,  -3,  -9, -10,  -5,   0,   2,  3,  3,  2,  0, -2, -5], //   0°
    [  9,   2,  -4,  -7,  -3,   2,   4,  4,  3,  1, -1, -3, -7], //  10°
    [ 12,   6,   1,  -3,   0,   3,   5,  5,  3,  1, -2, -5, -9], //  20°
    [ 13,   9,   5,   1,   3,   5,   6,  5,  3,  0, -3, -7,-11], //  30°
    [ 12,  10,   7,   4,   5,   6,   7,  6,  3, -1, -5, -9,-13], //  40°
    [ 10,   9,   8,   6,   6,   7,   7,  6,  2, -3, -8,-13,-17], //  50°
    [  7,   8,   8,   7,   7,   7,   7,  5,  1, -5,-12,-18,-23], //  60°
  ],
};

const bilinear = (lat, lon) => {
  const { lats, lons, vals } = DEC_GRID;
  // clamp
  const cLat = Math.max(lats[0], Math.min(lats[lats.length - 1], lat));
  const cLon = Math.max(lons[0], Math.min(lons[lons.length - 1], lon));

  let i = 0;
  while (i < lats.length - 1 && lats[i + 1] < cLat) i++;
  let j = 0;
  while (j < lons.length - 1 && lons[j + 1] < cLon) j++;

  const lat0 = lats[i], lat1 = lats[i + 1];
  const lon0 = lons[j], lon1 = lons[j + 1];
  const t = (cLat - lat0) / (lat1 - lat0);
  const u = (cLon - lon0) / (lon1 - lon0);
  const v00 = vals[i][j],     v01 = vals[i][j + 1];
  const v10 = vals[i + 1][j], v11 = vals[i + 1][j + 1];
  return (1 - t) * (1 - u) * v00
       + (1 - t) *      u  * v01
       +      t  * (1 - u) * v10
       +      t  *      u  * v11;
};

const cacheKey = (lat, lon) => {
  // round to 1° cell so the cache is reused for nearby calls
  const rLat = Math.round(lat);
  const rLon = Math.round(lon);
  return `falak.declination.v1.${rLat}.${rLon}`;
};

/**
 * Get magnetic declination (positive east) for the given lat/lon.
 * Tries cache → online API → local grid in that order.
 * Always resolves; never rejects.
 */
export async function getDeclinationDeg(lat, lon) {
  if (typeof lat !== 'number' || typeof lon !== 'number') return 0;

  // 1. cache
  try {
    const cached = window.localStorage.getItem(cacheKey(lat, lon));
    if (cached != null) {
      const val = parseFloat(cached);
      if (!Number.isNaN(val)) return val;
    }
  } catch { /* ignore */ }

  // 2. NOAA NGDC (CORS-enabled, returns JSON)
  try {
    const url = `https://www.ngdc.noaa.gov/geomag-web/calculators/calculateDeclination`
              + `?lat1=${lat}&lon1=${lon}&resultFormat=json&key=zNEw7`;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    const res = await fetch(url, { signal: ctrl.signal });
    clearTimeout(timer);
    if (res.ok) {
      const data = await res.json();
      const dec = data?.result?.[0]?.declination;
      if (typeof dec === 'number' && !Number.isNaN(dec)) {
        try { window.localStorage.setItem(cacheKey(lat, lon), String(dec)); } catch { /* ignore */ }
        return dec;
      }
    }
  } catch { /* fall through */ }

  // 3. local grid fallback
  const fallback = bilinear(lat, lon);
  try { window.localStorage.setItem(cacheKey(lat, lon), String(fallback)); } catch { /* ignore */ }
  return fallback;
}
